import type { CharacterAppearanceAssets, CharacterAnimationSequence } from './characterAppearance';

let appearanceAssetPromise: Promise<CharacterAppearanceAssets> | null = null;
let animationSetPromise: Promise<PlayerAnimationSet> | null = null;

export interface PlayerAnimationSet {
  /** Default unarmed/idle stand sequence id (808 in OSRS). */
  standId: number;
  weaponStandByItemId: Record<string, number>;
  weaponStandByInterface: Record<string, number>;
  sequencesById: Record<string, CharacterAnimationSequence>;
}

interface PlayerAnimationsFile {
  common?: {
    defaultLocomotion?: { stand?: number };
    unarmed?: { stand?: number };
  };
  weaponStandByItemId?: Record<string, number>;
  weaponStandByInterface?: Record<string, number>;
  weapons?: Array<{
    id: number;
    weaponInterface?: string | null;
    locomotion?: { stand?: number | null };
  }>;
  sequences: Record<string, CharacterAnimationSequence>;
}

export function loadCharacterAppearanceAssets(): Promise<CharacterAppearanceAssets> {
  if (!appearanceAssetPromise) {
    appearanceAssetPromise = Promise.all([
      fetchAppearanceJson<CharacterAppearanceAssets['manifest']>('/assets/character/character-appearance.json'),
      fetchAppearanceJson<{ models: CharacterAppearanceAssets['models'] }>('/assets/character/character-appearance-models.json'),
    ]).then(([manifest, modelFile]) => ({
      manifest: normalizeKnownManifestMismatches(manifest),
      models: modelFile.models,
    }));
  }

  return appearanceAssetPromise;
}

export function loadPlayerAnimations(): Promise<PlayerAnimationSet> {
  if (!animationSetPromise) {
    animationSetPromise = fetchAppearanceJson<PlayerAnimationsFile>(
      '/assets/character/player-animations.json',
    ).then((file) => {
      const weaponStandByItemId = { ...(file.weaponStandByItemId ?? {}) };
      const weaponStandByInterface = { ...(file.weaponStandByInterface ?? {}) };

      for (const weapon of file.weapons ?? []) {
        const stand = weapon.locomotion?.stand;
        if (typeof stand !== 'number') {
          continue;
        }
        weaponStandByItemId[String(weapon.id)] ??= stand;
        if (weapon.weaponInterface) {
          weaponStandByInterface[weapon.weaponInterface] ??= stand;
        }
      }

      return {
        standId: file.common?.defaultLocomotion?.stand
          ?? file.common?.unarmed?.stand
          ?? 808,
        weaponStandByItemId,
        weaponStandByInterface,
        sequencesById: file.sequences,
      };
    });
  }
  return animationSetPromise;
}

async function fetchAppearanceJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load appearance asset: ${url}`);
  }

  return response.json() as Promise<T>;
}

function normalizeKnownManifestMismatches(
  manifest: CharacterAppearanceAssets['manifest'],
): CharacterAppearanceAssets['manifest'] {
  const items = { ...manifest.items };

  // Local LIVE extraction has the Ancestral wearable trio shifted by one
  // tradeable id: 21021 is the robe top model, while 21024/21027 are the
  // robe bottom model. Correct the render metadata here without rewriting the
  // generated manifest asset.
  if (items['21021']) {
    items['21021'] = {
      ...items['21021'],
      name: 'Ancestral robe top',
      slot: 4,
      wearPos: { primary: 4, secondary: 6, tertiary: null },
    };
  }

  if (items['21024']) {
    items['21024'] = {
      ...items['21024'],
      name: 'Ancestral robe bottom',
      slot: 7,
      wearPos: { primary: 7, secondary: null, tertiary: null },
    };
  }

  if (items['21027']) {
    items['21027'] = {
      ...items['21027'],
      name: 'Ancestral robe bottom',
      slot: 7,
      wearPos: { primary: 7, secondary: null, tertiary: null },
    };
  }

  return { ...manifest, items };
}
